import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { StatusSummaryCards } from './components/StatusSummaryCards';
import { PipelineDataTable } from './components/PipelineDataTable';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import {
  businessTodayAsLocalDate,
  makePreset,
  type ReportPeriod
} from './date-range/reportPeriod';
import {
  PipelineLoan,
  PipelineStatus,
  StatusSummary,
  PipelineFilters,
  STATUS_CONFIG,
  derivePipelineStatus,
  getBorrowerName,
  getLoanOfficerName,
  getEffectiveFundedDate,
  getMilestoneDoneDate
} from './types';
import { buildBreakdown } from './components/fundedSummaryModel';
import { supabase } from '@/utils/supabase';
import { AlertTriangle, BarChart3, RefreshCw, Lock, Download } from 'lucide-react';
import * as ExcelJS from 'exceljs';
import { createComponentLogger } from '@/utils/logger';
import { parseISO, isBefore, isAfter, format } from 'date-fns';

const log = createComponentLogger('PipelineStatusReport', 'reports');
const STATUS_REPORT_FOLDERS = new Set(['Active', 'Completed Loans']);
const FETCH_PAGE_SIZE = 1000;
type RawPipelineLoan = Omit<PipelineLoan, 'broker_fund_date'>;
type PayoutFundDate = { id: string | null; loan_number: string | null; fund_date: string | null };

export function PipelineStatusReport() {
  const [loans, setLoans] = useState<PipelineLoan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payoutWarning, setPayoutWarning] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [period, setPeriod] = useState<ReportPeriod>(() => (
    makePreset('mtd', businessTodayAsLocalDate())
  ));
  // Date range now lives in `period`; the rest of the filters keep their shared shape.
  const [filters, setFilters] = useState<Omit<PipelineFilters, 'dateRange'>>({
    status: 'all',
    loanOfficer: 'all',
    investor: 'all',
    channel: 'all',
    lockedOnly: false
  });

  // Fetch loans from database
  const fetchLoans = async () => {
    const today = businessTodayAsLocalDate();
    setIsLoading(true);
    setLoadError(null);
    setPayoutWarning(null);
    setPeriod((current) => (
      current.mode === 'mtd' || current.mode === 'qtd' || current.mode === 'ytd'
        ? makePreset(current.mode, today)
        : current
    ));
    try {
      const loanSelect = `
        encompass_loan_id,
        trans_details_loan_number,
        borrower_first_name,
        borrower_last_name,
        loan_officer_first_name,
        loan_officer_last_name,
        trans_details_loan_amt,
        rate_lock_buy_side_lock_date,
        rate_lock_sell_side_lock_date,
        rate_lock_request_lock_date,
        rate_lock_buy_side_lock_expires,
        rate_lock_status,
        trans_details_interest_rate,
        rate_lock_loan_program,
        rate_lock_investor_commitment,
        investor_name,
        milestone,
        loan_folder,
        channel,
        creation_date,
        application_date,
        closing_date,
        funded_date,
        purchased_date,
        cx_ls_date_processing,
        cx_ls_date_uw,
        cd_sent_date,
        milestone_history,
        last_conversation_log_at,
        milestone_date,
        shipping_investor_delivery_date,
        loan_processor_first_name,
        loan_processor_last_name,
        underwriter_first_name,
        underwriter_last_name,
        closer_first_name,
        closer_last_name,
        loan_officer_assistant_first_name,
        loan_officer_assistant_last_name,
        loan_officer_2_first_name,
        loan_officer_2_last_name,
        trans_details_loan_type,
        trans_details_loan_purpose,
        freddie_mac_ltv_ratio_pct,
        trans_details_cltv_ratio_pct,
        lien_position
      `;

      // Fetch encompass loans
      const loansData: RawPipelineLoan[] = [];
      for (let from = 0; ; from += FETCH_PAGE_SIZE) {
        const { data, error } = await supabase
          .from('encompass_loans')
          .select(loanSelect)
          .not('loan_folder', 'in', '("Test","(Trash)","Transferred Data")')
          // Null-safe: keep false AND null, drop only soft-deleted (=true) loans.
          .not('deleted_from_encompass', 'is', true)
          .order('trans_details_loan_number', { ascending: false })
          .range(from, from + FETCH_PAGE_SIZE - 1);

        if (error) {
          log.error('Failed to fetch loans', { error });
          setLoadError('Unable to load the current pipeline. Refresh to try again.');
          return;
        }

        loansData.push(...((data || []) as RawPipelineLoan[]));
        if (!data || data.length < FETCH_PAGE_SIZE) {
          break;
        }
      }

      // Fetch broker fund dates from payouts table
      const payoutsData: PayoutFundDate[] = [];
      for (let from = 0; ; from += FETCH_PAGE_SIZE) {
        const { data, error } = await supabase
          .from('payouts')
          .select('id, loan_number, fund_date')
          .not('fund_date', 'is', null)
          .order('fund_date', { ascending: true })
          .order('id', { ascending: true })
          .range(from, from + FETCH_PAGE_SIZE - 1);

        if (error) {
          log.warn('Failed to fetch broker fund dates', { error });
          setPayoutWarning('Broker payout dates are unavailable. Brokered terminal-stage dates may be incomplete.');
          payoutsData.length = 0;
          break;
        }

        payoutsData.push(...((data || []) as PayoutFundDate[]));
        if (!data || data.length < FETCH_PAGE_SIZE) {
          break;
        }
      }

      // Create a map of loan_number -> fund_date
      const fundDateMap = new Map<string, string>();
      payoutsData.forEach((payout) => {
        if (payout.loan_number && payout.fund_date && !fundDateMap.has(payout.loan_number)) {
          fundDateMap.set(payout.loan_number, payout.fund_date);
        }
      });

      // Merge broker fund dates and ensure classification fields into loans
      const loansWithFundDates = (loansData || []).map((loan) => ({
        ...loan,
        broker_fund_date: loan.trans_details_loan_number
          ? fundDateMap.get(loan.trans_details_loan_number) || null
          : null,
        trans_details_loan_type: loan.trans_details_loan_type || null,
        trans_details_loan_purpose: loan.trans_details_loan_purpose || null
      }));

      setLoans(loansWithFundDates);
      setRefreshedAt(new Date());
      log.info('Fetched pipeline loans', { count: loansWithFundDates.length });
    } catch (error) {
      setLoadError('Unable to load the current pipeline. Refresh to try again.');
      log.error('Error fetching loans', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  // Get unique loan officers for filter dropdown
  const loanOfficers = useMemo(() => {
    const officers = new Set<string>();
    loans.forEach((loan) => {
      const loName = getLoanOfficerName(loan);
      if (loName && loName !== '-') {
        officers.add(loName);
      }
    });
    return Array.from(officers).sort();
  }, [loans]);

  // Get unique investors for filter dropdown
  const investors = useMemo(() => {
    const invs = new Set<string>();
    loans.forEach((loan) => {
      if (loan.investor_name) {
        invs.add(loan.investor_name);
      }
    });
    return Array.from(invs).sort();
  }, [loans]);

  // Get unique channels for filter dropdown
  const channels = useMemo(() => {
    const chans = new Set<string>();
    loans.forEach((loan) => {
      if (loan.channel) {
        chans.add(loan.channel);
      }
    });
    return Array.from(chans).sort();
  }, [loans]);

  const statusReportLoans = useMemo(
    () => loans.filter((loan) => STATUS_REPORT_FOLDERS.has(loan.loan_folder)),
    [loans]
  );

  // Snapshot entity filters, applied before the terminal-stage date window.
  const passesEntityFilters = useMemo(() => {
    return (loan: PipelineLoan): boolean => {
      if (filters.lockedOnly) {
        const lockStatus = loan.rate_lock_status?.toLowerCase() || '';
        if (lockStatus !== 'locked') {
          return false;
        }
      }
      // LO filter
      if (filters.loanOfficer !== 'all' && getLoanOfficerName(loan) !== filters.loanOfficer) {
        return false;
      }
      // Investor filter
      if (filters.investor !== 'all' && loan.investor_name !== filters.investor) {
        return false;
      }
      // Channel filter
      if (filters.channel !== 'all' && loan.channel !== filters.channel) {
        return false;
      }
      return true;
    };
  }, [filters.lockedOnly, filters.loanOfficer, filters.investor, filters.channel]);

  // Base filtered loans (entity filters + funded/purchased date window).
  // Used for the Status summary cards and the Loan Details table.
  const baseFilteredLoans = useMemo(() => {
    return statusReportLoans.filter((loan) => {
      // Date filter - only applies to funded/purchased loans.
      // Active (non-funded) loans are always shown; funded/purchased only if
      // their effective date is within the selected window.
      const status = derivePipelineStatus(loan);
      if (status === 'funded' || status === 'purchased') {
        const { from, to } = period;
        const effectiveDate = status === 'purchased'
          ? (loan.purchased_date || getEffectiveFundedDate(loan))
          : getEffectiveFundedDate(loan);
        if (from && effectiveDate) {
          const parsedDate = parseISO(effectiveDate);
          if (isBefore(parsedDate, from)) {
            return false;
          }
          if (to && isAfter(parsedDate, to)) {
            return false;
          }
        }
      }
      return passesEntityFilters(loan);
    });
  }, [statusReportLoans, passesEntityFilters, period]);

  // Filter loans based on current filters (including status)
  const filteredLoans = useMemo(() => {
    return baseFilteredLoans.filter((loan) => {
      // Status filter
      if (filters.status !== 'all') {
        const loanStatus = derivePipelineStatus(loan);
        if (loanStatus !== filters.status) {
return false;
}
      }
      return true;
    });
  }, [baseFilteredLoans, filters.status]);

  // Calculate status summaries (uses baseFilteredLoans so cards reflect all filters except status)
  const statusSummaries = useMemo((): StatusSummary[] => {
    const summaryMap = new Map<PipelineStatus, { count: number; totalAmount: number }>();

    // Initialize all statuses
    Object.keys(STATUS_CONFIG).forEach((status) => {
      summaryMap.set(status as PipelineStatus, { count: 0, totalAmount: 0 });
    });

    // Calculate summaries from base filtered loans
    baseFilteredLoans.forEach((loan) => {
      const status = derivePipelineStatus(loan);
      const existing = summaryMap.get(status)!;
      existing.count += 1;
      existing.totalAmount += loan.trans_details_loan_amt || 0;
    });

    // Convert to array
    return Object.entries(STATUS_CONFIG).map(([status, config]) => {
      const data = summaryMap.get(status as PipelineStatus)!;
      return {
        status: status as PipelineStatus,
        label: status === 'funded'
          ? 'At Funded'
          : status === 'purchased'
            ? 'At Purchased'
            : config.label,
        count: data.count,
        totalAmount: data.totalAmount,
        color: config.color
      };
    });
  }, [baseFilteredLoans]);

  // Helper for date formatting in export
  const formatDateForExport = (dateStr: string | null): string => {
    if (!dateStr) {
return '';
}
    try {
      return format(parseISO(dateStr), 'MM/dd/yyyy');
    } catch {
      return '';
    }
  };

  // Build filter summary for export context row
  const buildFilterSummary = (): string => {
    const parts: string[] = [];
    parts.push(`As of: ${format(new Date(), 'MM/dd/yyyy')}`);
    parts.push(period.mode === 'custom'
      ? `${format(period.from, 'MM/dd/yyyy')} – ${format(period.to, 'MM/dd/yyyy')}`
      : period.label);
    if (filters.loanOfficer !== 'all') {
parts.push(`LO: ${filters.loanOfficer}`);
}
    if (filters.investor !== 'all') {
parts.push(`Investor: ${filters.investor}`);
}
    if (filters.channel !== 'all') {
parts.push(`Channel: ${filters.channel}`);
}
    if (filters.lockedOnly) {
parts.push('Currently Locked');
}
    if (filters.status !== 'all') {
parts.push(`Status: ${STATUS_CONFIG[filters.status].label}`);
}
    return parts.join(' | ');
  };

  // Export pipeline report to Excel (Summary + Details sheets)
  const handleExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'LendWise';
      workbook.created = new Date();

      // --- Styling constants ---
      const headerFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      const boldFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
      const normalFont: Partial<ExcelJS.Font> = { size: 10 };
      const thinBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FF000000' } };
      const lightBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FFD0D0D0' } };

      const applyBoxBorders = (sheet: ExcelJS.Worksheet, startRow: number, endRow: number, startCol: number, endCol: number) => {
        for (let r = startRow; r <= endRow; r++) {
          for (let c = startCol; c <= endCol; c++) {
            sheet.getCell(r, c).border = {
              top: r === startRow ? thinBorder : lightBorder,
              bottom: r === endRow ? thinBorder : lightBorder,
              left: c === startCol ? thinBorder : lightBorder,
              right: c === endCol ? thinBorder : lightBorder
            };
          }
        }
      };

      // --- Summary Sheet ---
      const summary = workbook.addWorksheet('Summary');
      summary.getColumn(1).width = 22;
      summary.getColumn(2).width = 10;
      summary.getColumn(2).alignment = { horizontal: 'center' };
      summary.getColumn(3).width = 18;
      summary.getColumn(3).alignment = { horizontal: 'center' };
      summary.getColumn(4).width = 16;
      summary.getColumn(4).alignment = { horizontal: 'center' };

      // Row 1: Company name
      const companyRow = summary.addRow(['Lendwise Mortgage Inc.']);
      companyRow.font = { bold: true, size: 14 };
      summary.mergeCells('A1:D1');

      // Row 2: Title
      const titleRow = summary.addRow(['Pipeline Status Report']);
      titleRow.font = { bold: true, size: 12 };
      summary.mergeCells('A2:D2');

      // Row 3: Filter context
      const contextRow = summary.addRow([buildFilterSummary()]);
      contextRow.font = { size: 9, italic: true };
      summary.mergeCells('A3:D3');

      // Row 4: blank
      summary.addRow([]);

      // Row 5: Header
      const summaryHeaderRow = summary.addRow(['Status', 'Count', 'Volume ($)', '% by Volume']);
      summaryHeaderRow.font = headerFont;
      summaryHeaderRow.fill = headerFill;

      // Exclude "Started" from export — not actionable data
      const exportSummaries = statusSummaries.filter((s) => s.status !== 'started');
      const exportBaseLoans = baseFilteredLoans.filter((l) => derivePipelineStatus(l) !== 'started');
      const exportDetailLoans = filteredLoans.filter((l) => derivePipelineStatus(l) !== 'started');

      // Compute total volume for percentages
      const totalVolume = exportSummaries.reduce((sum, s) => sum + s.totalAmount, 0);

      // Data rows (one per status, excluding Started)
      for (const s of exportSummaries) {
        const pct = totalVolume > 0 ? s.totalAmount / totalVolume : 0;
        const dataRow = summary.addRow([s.label, s.count, s.totalAmount, pct]);
        dataRow.font = normalFont;
        dataRow.getCell(3).numFmt = '#,##0';
        dataRow.getCell(4).numFmt = '0.0%';
      }

      // TOTAL row
      const totalCount = exportSummaries.reduce((sum, s) => sum + s.count, 0);
      const totalRow = summary.addRow(['TOTAL', totalCount, totalVolume, totalVolume > 0 ? 1 : 0]);
      totalRow.font = boldFont;
      totalRow.getCell(3).numFmt = '#,##0';
      totalRow.getCell(4).numFmt = '0.0%';

      // Box borders around table (header through total)
      applyBoxBorders(summary, summaryHeaderRow.number, totalRow.number, 1, 4);

      // --- Loan Type Breakdown ---
      const PIPELINE_LOAN_TYPE_LABELS = ['Non-QM', 'Conventional', 'FHA/VA', 'Second Liens'];
      const getPipelineLoanType = (loan: PipelineLoan): string => {
        if (loan.lien_position === 'Second Lien') {
return 'Second Liens';
}
        const type = (loan.trans_details_loan_type || '').toLowerCase().trim();
        const program = (loan.rate_lock_loan_program || '').toLowerCase().trim();
        const investor = (loan.investor_name || '').toLowerCase().trim();
        if (program.includes('non-qm')) {
return 'Non-QM';
}
        if (investor.includes('vista point')) {
return 'Non-QM';
}
        if (investor.includes('eresi') && !program.includes('agency')) {
return 'Non-QM';
}
        if (investor.includes('luxury')) {
return 'Non-QM';
}
        if (investor.includes('clearedge')) {
return 'Non-QM';
}
        if (type === 'va' || type === 'fha' || type === 'usda') {
return 'FHA/VA';
}
        if (type === 'conventional') {
return 'Conventional';
}
        return 'Second Liens';
      };

      const loanTypeRows = buildBreakdown(exportBaseLoans, getPipelineLoanType, PIPELINE_LOAN_TYPE_LABELS);
      summary.addRow([]); // blank separator
      const ltHeaderRow = summary.addRow(['Loan Type', 'Count', 'Volume ($)', '% by Volume']);
      ltHeaderRow.font = headerFont;
      ltHeaderRow.fill = headerFill;
      for (const r of loanTypeRows) {
        const dr = summary.addRow([r.label, r.units, r.volume, r.pct / 100]);
        dr.font = normalFont;
        dr.getCell(3).numFmt = '#,##0';
        dr.getCell(4).numFmt = '0.0%';
      }
      const ltTotalVol = loanTypeRows.reduce((s, r) => s + r.volume, 0);
      const ltTotalUnits = loanTypeRows.reduce((s, r) => s + r.units, 0);
      const ltTotalRow = summary.addRow(['TOTAL', ltTotalUnits, ltTotalVol, ltTotalVol > 0 ? 1 : 0]);
      ltTotalRow.font = boldFont;
      ltTotalRow.getCell(3).numFmt = '#,##0';
      ltTotalRow.getCell(4).numFmt = '0.0%';
      applyBoxBorders(summary, ltHeaderRow.number, ltTotalRow.number, 1, 4);

      // --- Purpose Breakdown ---
      const PIPELINE_PURPOSE_LABELS = ['Purchase', 'Rate/Term', 'Cash-Out'];
      const getPipelinePurpose = (loan: PipelineLoan): string => {
        const purpose = (loan.trans_details_loan_purpose || '').toLowerCase().trim();
        if (purpose.includes('purchase')) {
return 'Purchase';
}
        if (purpose.includes('cash-out') && !purpose.includes('nocash')) {
return 'Cash-Out';
}
        return 'Rate/Term';
      };

      const purposeRows = buildBreakdown(exportBaseLoans, getPipelinePurpose, PIPELINE_PURPOSE_LABELS);
      summary.addRow([]); // blank separator
      const prHeaderRow = summary.addRow(['Purpose', 'Count', 'Volume ($)', '% by Volume']);
      prHeaderRow.font = headerFont;
      prHeaderRow.fill = headerFill;
      for (const r of purposeRows) {
        const dr = summary.addRow([r.label, r.units, r.volume, r.pct / 100]);
        dr.font = normalFont;
        dr.getCell(3).numFmt = '#,##0';
        dr.getCell(4).numFmt = '0.0%';
      }
      const prTotalVol = purposeRows.reduce((s, r) => s + r.volume, 0);
      const prTotalUnits = purposeRows.reduce((s, r) => s + r.units, 0);
      const prTotalRow = summary.addRow(['TOTAL', prTotalUnits, prTotalVol, prTotalVol > 0 ? 1 : 0]);
      prTotalRow.font = boldFont;
      prTotalRow.getCell(3).numFmt = '#,##0';
      prTotalRow.getCell(4).numFmt = '0.0%';
      applyBoxBorders(summary, prHeaderRow.number, prTotalRow.number, 1, 4);

      // --- Details Sheet ---
      const details = workbook.addWorksheet('Pipeline Details');
      const detailHeaders = [
        'Lock Date', 'Loan #', 'Borrower', 'Amount',
        'Investor', 'Commitment #', 'Rate', 'LTV', 'CLTV', 'Lock Expires',
        'Status', 'Status Date', 'Channel', 'Loan Type', 'Purpose'
      ];
      const detailHeaderRow = details.addRow(detailHeaders);
      detailHeaderRow.font = headerFont;
      detailHeaderRow.fill = headerFill;

      // Column widths
      const detailWidths = [12, 14, 22, 14, 22, 16, 10, 10, 10, 12, 16, 12, 14, 16, 14];
      detailWidths.forEach((w, i) => {
        details.getColumn(i + 1).width = w;
      });

      // Data rows (excluding Started)
      exportDetailLoans.forEach((loan) => {
        const status = derivePipelineStatus(loan);
        const row = details.addRow([
          formatDateForExport(loan.rate_lock_buy_side_lock_date),
          loan.trans_details_loan_number || '',
          getBorrowerName(loan),
          loan.trans_details_loan_amt || 0,
          loan.investor_name || '',
          loan.rate_lock_investor_commitment || '',
          loan.trans_details_interest_rate || 0,
          loan.freddie_mac_ltv_ratio_pct || 0,
          loan.trans_details_cltv_ratio_pct || 0,
          formatDateForExport(loan.rate_lock_buy_side_lock_expires),
          STATUS_CONFIG[status].label,
          formatDateForExport(getMilestoneDoneDate(loan, status)),
          loan.channel || '',
          getPipelineLoanType(loan),
          getPipelinePurpose(loan)
        ]);
        row.font = normalFont;
      });

      // Number formatting for detail columns
      details.getColumn(4).numFmt = '#,##0';       // Amount
      details.getColumn(7).numFmt = '0.000"%"';     // Rate (literal %, not Excel percentage)
      details.getColumn(8).numFmt = '0.000"%"';     // LTV
      details.getColumn(9).numFmt = '0.000"%"';    // CLTV

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PipelineStatusSnapshot_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      log.info('Exported pipeline status report', {
        summaryStatuses: statusSummaries.length,
        detailCount: exportDetailLoans.length
      });
    } catch (error) {
      log.error('Failed to export pipeline status report', {}, error as Error);
    }
  };

  const handleStatusClick = (status: PipelineStatus | 'all') => {
    setFilters((prev) => ({ ...prev, status }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Pipeline Status Report
          </h1>
          <p className="text-muted-foreground">
            Current pipeline snapshot; the date range applies only to terminal funded and purchased stages.
          </p>
          {refreshedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Refreshed {format(refreshedAt, 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading || filteredLoans.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchLoans}
            disabled={isLoading}
            aria-label="Refresh pipeline status"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {loadError}
        </div>
      )}
      {payoutWarning && (
        <div role="status" className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-foreground">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          {payoutWarning}
        </div>
      )}

      {/* Shared Filters */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-4">
          {/* Date Range Picker */}
          <ReportDateRangePicker
            value={period}
            onChange={setPeriod}
            max={businessTodayAsLocalDate()}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* LO Filter */}
          <Select
            value={filters.loanOfficer}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, loanOfficer: value }))
            }
          >
            <SelectTrigger className="w-full min-w-[180px] sm:w-[180px]">
              <SelectValue placeholder="All Loan Officers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Loan Officers</SelectItem>
              {loanOfficers.map((lo) => (
                <SelectItem key={lo} value={lo}>
                  {lo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Investor Filter */}
          <Select
            value={filters.investor}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, investor: value }))
            }
          >
            <SelectTrigger className="w-full min-w-[180px] sm:w-[200px]">
              <SelectValue placeholder="All Investors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Investors</SelectItem>
              {investors.map((investor) => (
                <SelectItem key={investor} value={investor}>
                  {investor}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Channel Filter */}
          <Select
            value={filters.channel}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, channel: value }))
            }
          >
            <SelectTrigger className="w-full min-w-[160px] sm:w-[160px]">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map((channel) => (
                <SelectItem key={channel} value={channel}>
                  {channel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Current lock-state toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="locked-only"
              checked={filters.lockedOnly}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({ ...prev, lockedOnly: checked }))
              }
            />
            <Label htmlFor="locked-only" className="flex items-center gap-1 cursor-pointer">
              <Lock className="h-4 w-4" />
              Currently Locked
            </Label>
          </div>
        </div>
      </div>

      <StatusSummaryCards
        summaries={statusSummaries}
        selectedStatus={filters.status}
        onStatusClick={handleStatusClick}
      />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Current Pipeline Loans</CardTitle>
        </CardHeader>
        <CardContent>
          <PipelineDataTable loans={filteredLoans} isLoading={isLoading} />
        </CardContent>
      </Card>
    </div>
  );
}

export default PipelineStatusReport;
